import { useCallback, useMemo, useRef } from 'react'
import { useAuth, invalidateCachedFetch, useRealtimeSync } from '@structural-climbing/auth'
import type { RealtimeBinding } from '@structural-climbing/auth'
import * as Sentry from '@sentry/react'

/**
 * How long the channel has to stay down, with the member actually looking at a
 * connected device, before it counts as an incident rather than a phone waking up.
 */
const OUTAGE_REPORT_AFTER_MS = 2 * 60_000

/**
 * Everything the gym can change under a member's feet, watched from the shell.
 *
 * Mounted in RootLayout because the member can be anywhere in the app when the
 * desk sells them a plan, an owner publishes a competition or a news post goes
 * up — and because the shell never unmounts, so the channel is not rebuilt on
 * every navigation.
 *
 * No `subscriptions` binding: the app role cannot read that table, and Realtime
 * refuses a filter on a column the role cannot read, which fails the whole
 * channel ("invalid column for filter user_id"). A plan sold at the desk still
 * shows up: the check-in channel invalidates `my-subscription:`, and every
 * foreground re-reads it.
 *
 * The two gym-scoped tables are bound without a filter on purpose. postgres_changes
 * filters support eq/neq/lt/lte/gt/gte/in but not `is`, so global news
 * (`gym_id IS NULL`) cannot be expressed as one; RLS does the scoping instead,
 * which also keeps the binding set independent of which gyms the member belongs
 * to, so the channel never rebuilds when that list resolves. Both tables are
 * tiny and change a handful of times a week.
 */
export function useMemberRealtime() {
  const { user, isAuthenticated } = useAuth()
  const userId = user?.sub as string | undefined
  const channel = isAuthenticated && userId ? `member-${userId}` : null

  const bindings = useMemo<RealtimeBinding[]>(
    () =>
      userId
        ? [
            { table: 'join_requests', filter: `user_id=eq.${userId}` },
            { table: 'news' },
            { table: 'competitions' },
          ]
        : [],
    [userId],
  )

  // Which gyms they belong to decides what the Home tab resolves to, and the
  // nav, the switcher and every "do I have a gym" branch read the same key.
  const resync = useCallback(() => {
    invalidateCachedFetch('my-subscription:')
    invalidateCachedFetch('my-gyms')
    invalidateCachedFetch('my-profile')
    invalidateCachedFetch('news:')
    invalidateCachedFetch('competitions:')
  }, [])

  const onChange = useCallback(
    (payload: { table?: string }) => {
      switch (payload?.table) {
        case 'join_requests':
          invalidateCachedFetch('my-gyms')
          invalidateCachedFetch('my-profile')
          break
        case 'news':
          invalidateCachedFetch('news:')
          break
        case 'competitions':
          invalidateCachedFetch('competitions:')
          break
        default:
          resync()
      }
    },
    [resync],
  )

  // A silently dead socket is exactly the bug this replaces, so it has to reach
  // Sentry — but most drops are not incidents. iOS kills the WebSocket every time
  // the app is backgrounded or the phone changes cell, and useRealtimeSync retries
  // for as long as it stays down, so reporting each attempt turned one commute
  // into dozens of identical events from a member who saw nothing wrong.
  //
  // So the clock only runs while the member is looking at a connected device, and
  // a sustained outage is reported once, not once per retry.
  const downSince = useRef<number | null>(null)
  const reported = useRef(false)

  const onStatus = useCallback(
    (status: string, err?: Error) => {
      if (status === 'SUBSCRIBED') {
        downSince.current = null
        reported.current = false
        return
      }
      // CLOSED is our own teardown — a rejoin or an unmount, never a failure.
      if (status === 'CLOSED') return

      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (hidden || offline) {
        downSince.current = null
        return
      }

      const now = Date.now()
      if (downSince.current === null) {
        downSince.current = now
        return
      }
      const downForMs = now - downSince.current
      if (reported.current || downForMs < OUTAGE_REPORT_AFTER_MS) return
      reported.current = true
      Sentry.captureMessage('realtime channel error', {
        level: 'warning',
        extra: { channel, status, error: err?.message, downForMs },
      })
    },
    [channel],
  )

  useRealtimeSync({ channel, bindings, onChange, onResync: resync, onStatus })
}
